import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useWallet } from '../../contexts/WalletContext';
import { useToast } from '../../contexts/ToastContext';
import { CopyableAddress } from '../common/CopyButton';
import SafeBalanceDisplay from '../common/SafeBalanceDisplay';
import '../../styles/trading-modal.css';

const QuickTradeModal = ({
  isOpen,
  onClose,
  token,
  tradeType = 'buy',
  userTokenBalance = null
}) => {
  const { tokens, isAuthenticated } = useAuth();
  const { wallets, activeWallet } = useWallet();
  const { showSuccess, showError } = useToast();

  // Modal state
  const [currentStep, setCurrentStep] = useState('configure'); // configure, preview, executing, success
  const [loading, setLoading] = useState(false);

  // Trade configuration - simplified for quick trading
  const [tradeConfig, setTradeConfig] = useState({
    type: tradeType,
    walletId: '',
    amount: '',
    amountType: 'custom', // custom, percentage
    percentage: 50,
    slippageBps: 50, // 0.5%
    priorityFee: 5000
  });

  // Real-time data
  const [walletBalances, setWalletBalances] = useState({});
  const [tokenInfo, setTokenInfo] = useState(null);
  const [priceQuote, setPriceQuote] = useState(null);
  const [tradingFees, setTradingFees] = useState(null);
  const [tradePreview, setTradePreview] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepump.urion.live/api';

  // Get platform wallets for trading
  const platformWallets = wallets.filter(w => w.wallet_type === 'platform');
  const selectedWallet = platformWallets.find(w => w.id.toString() === tradeConfig.walletId);

  // Authenticated request helper
  const makeAuthenticatedRequest = async (url, options = {}) => {
    if (!tokens?.access_token) {
      throw new Error('Please log in to trade');
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  };

  // Initialize modal when opened - pre-filled with token information
  useEffect(() => {
    if (isOpen && token) {
      setCurrentStep('configure');
      setTradeConfig(prev => ({
        ...prev,
        type: tradeType,
        walletId: activeWallet?.wallet_type === 'platform' ? activeWallet.id.toString() :
          platformWallets.length > 0 ? platformWallets[0].id.toString() : ''
      }));

      // Pre-fill token information from discovery context
      setTokenInfo({
        symbol: token.symbol,
        name: token.name,
        address: token.address,
        priceUSD: token.price,
        marketCap: token.marketCap,
        imageUrl: token.imageUrl || token.logo,
        isVerified: token.isVerified || false
      });

      fetchWalletBalances();
    }
  }, [isOpen, token, tradeType, activeWallet, platformWallets]);

  // Fetch wallet balances
  const fetchWalletBalances = async () => {
    if (!isAuthenticated || platformWallets.length === 0) return;

    const balances = {};

    for (const wallet of platformWallets) {
      try {
        const response = await makeAuthenticatedRequest(`/wallets/${wallet.id}/balance`);
        if (response.success) {
          balances[wallet.id] = response.data.balance;
        }
      } catch (error) {
        console.warn(`Could not fetch balance for wallet ${wallet.id}:`, error.message);
        balances[wallet.id] = { sol: 0 };
      }
    }

    setWalletBalances(balances);
  };

  // Calculate trade amount based on configuration
  const calculateTradeAmount = useCallback(() => {
    if (!selectedWallet || !walletBalances[selectedWallet.id]) return 0;

    const balance = walletBalances[selectedWallet.id];

    if (tradeConfig.amountType === 'percentage') {
      if (tradeConfig.type === 'buy') {
        // Calculate percentage of SOL balance (minus fees)
        const maxAmount = Math.max(0, balance.sol - 0.02);
        return (maxAmount * tradeConfig.percentage / 100);
      } else {
        // For sell, use percentage of token balance
        return userTokenBalance ? (userTokenBalance * tradeConfig.percentage / 100) : 0;
      }
    } else {
      return parseFloat(tradeConfig.amount) || 0;
    }
  }, [selectedWallet, walletBalances, tradeConfig, userTokenBalance]);

  // Get price quote and trade preview with real-time updates
  const updateTradePreview = useCallback(async () => {
    const amount = calculateTradeAmount();
    if (!amount || !selectedWallet || !token) return;

    try {
      setLoading(true);

      // Get price quote from Jupiter
      const quoteResponse = await fetch(`${API_BASE_URL}/trading/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inputMint: tradeConfig.type === 'buy' ? 'So11111111111111111111111111111111111111112' : token.address,
          outputMint: tradeConfig.type === 'buy' ? token.address : 'So11111111111111111111111111111111111111112',
          amount: tradeConfig.type === 'buy' ? Math.floor(parseFloat(amount) * 1000000000) : Math.floor(parseFloat(amount) * 1000000),
          slippageBps: tradeConfig.slippageBps
        })
      });

      const quoteData = await quoteResponse.json();
      if (quoteData.success) {
        setPriceQuote(quoteData.data.quote);

        // Calculate trading fees
        const feesResponse = await fetch(`${API_BASE_URL}/token-info/trading-fees`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            inputMint: tradeConfig.type === 'buy' ? 'So11111111111111111111111111111111111111112' : token.address,
            outputMint: tradeConfig.type === 'buy' ? token.address : 'So11111111111111111111111111111111111111112',
            amount: amount
          })
        });

        const feesData = await feesResponse.json();
        if (feesData.success) {
          setTradingFees(feesData.data.fees);
        }

        // Create trade preview
        setTradePreview({
          inputAmount: amount,
          outputAmount: tradeConfig.type === 'buy' ?
            (quoteData.data.quote.outputAmount / 1000000) :
            (quoteData.data.quote.outputAmount / 1000000000),
          priceImpact: quoteData.data.quote.priceImpact || 0,
          exchangeRate: quoteData.data.quote.exchangeRate || 0,
          minimumReceived: quoteData.data.quote.outputAmount * (1 - tradeConfig.slippageBps / 10000),
          route: quoteData.data.quote.route || []
        });
      }
    } catch (error) {
      console.warn('Could not get trade preview:', error.message);
    } finally {
      setLoading(false);
    }
  }, [calculateTradeAmount, selectedWallet, token, tradeConfig, API_BASE_URL]);

  // Update preview when configuration changes (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (currentStep === 'configure' && calculateTradeAmount() > 0) {
        updateTradePreview();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [updateTradePreview, currentStep, calculateTradeAmount]);

  // Execute trade with portfolio update callbacks
  const executeTrade = async () => {
    if (!selectedWallet || !tradePreview) return;

    setCurrentStep('executing');
    setLoading(true);

    try {
      const endpoint = tradeConfig.type === 'buy' ? '/trading/quick-buy' : '/trading/quick-sell';
      const amount = calculateTradeAmount();

      const result = await makeAuthenticatedRequest(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          tokenAddress: token.address,
          walletId: selectedWallet.id,
          [tradeConfig.type === 'buy' ? 'solAmount' : 'tokenAmount']: amount,
          slippageBps: tradeConfig.slippageBps,
          priorityFee: tradeConfig.priorityFee,
          source: 'discovery' // Track source for analytics
        })
      });

      if (result.success) {
        setCurrentStep('success');
        showSuccess(`${tradeConfig.type === 'buy' ? 'Buy' : 'Sell'} order completed successfully!`);

        // Refresh balances and trigger portfolio update callback
        await fetchWalletBalances();
      } else {
        throw new Error(result.error?.message || 'Trade failed');
      }
    } catch (error) {
      showError(`Trade failed: ${error.message}`);
      setCurrentStep('configure');
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close with success/error handling
  const handleClose = () => {
    if (currentStep === 'executing') return; // Prevent closing during execution

    setCurrentStep('configure');
    setTradeConfig(prev => ({ ...prev, amount: '', percentage: 50 }));
    setPriceQuote(null);
    setTradingFees(null);
    setTradePreview(null);
    onClose();
  };

  // Handle step navigation
  const goToPreview = () => {
    const amount = calculateTradeAmount();
    if (amount <= 0) {
      showError('Please enter a valid trade amount');
      return;
    }

    // Validate balance for sell orders
    if (tradeConfig.type === 'sell' && userTokenBalance && amount > userTokenBalance) {
      showError(`Insufficient ${token.symbol} balance. Available: ${userTokenBalance.toFixed(6)}`);
      return;
    }

    setCurrentStep('preview');
  };

  const goBackToConfig = () => {
    setCurrentStep('configure');
  };

  if (!isOpen) return null;

  return (
    <div className="trading-modal-overlay quick-trade" onClick={handleClose}>
      <div className="trading-modal quick-trade-modal" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header - Pre-filled with token information */}
        <div className="trading-modal-header">
          <div className="modal-title">
            <span className={`trade-type-badge ${tradeConfig.type}`}>
              {tradeConfig.type === 'buy' ? '📈' : '📉'} QUICK {tradeConfig.type.toUpperCase()}
            </span>
            <div className="token-info">
              <img
                src={tokenInfo?.imageUrl}
                alt={tokenInfo?.name}
                className="token-logo-small"
                onError={(e) => {
                  e.target.src = `https://via.placeholder.com/24x24/6366f1/ffffff?text=${tokenInfo?.symbol?.charAt(0) || '?'}`;
                }}
              />
              <div className="token-details">
                <span className="token-symbol">{tokenInfo?.symbol}</span>
                <span className="token-name">{tokenInfo?.name}</span>
              </div>
            </div>
          </div>
          <button className="modal-close-btn" onClick={handleClose} disabled={currentStep === 'executing'}>
            ×
          </button>
        </div>

        {/* Simplified Step Indicator */}
        <div className="step-indicator quick-trade">
          <div className={`step ${currentStep === 'configure' ? 'active' : currentStep !== 'configure' ? 'completed' : ''}`}>
            <span className="step-number">1</span>
            <span className="step-label">Configure</span>
          </div>
          <div className={`step ${currentStep === 'preview' ? 'active' : currentStep === 'executing' || currentStep === 'success' ? 'completed' : ''}`}>
            <span className="step-number">2</span>
            <span className="step-label">Preview</span>
          </div>
          <div className={`step ${currentStep === 'executing' ? 'active' : currentStep === 'success' ? 'completed' : ''}`}>
            <span className="step-number">3</span>
            <span className="step-label">Execute</span>
          </div>
        </div>

        {/* Modal Content */}
        <div className="trading-modal-content">
          {currentStep === 'configure' && (
            <QuickConfigureStep
              tradeConfig={tradeConfig}
              setTradeConfig={setTradeConfig}
              platformWallets={platformWallets}
              walletBalances={walletBalances}
              token={token}
              tokenInfo={tokenInfo}
              userTokenBalance={userTokenBalance}
              calculateTradeAmount={calculateTradeAmount}
              onNext={goToPreview}
              loading={loading}
            />
          )}

          {currentStep === 'preview' && (
            <QuickPreviewStep
              tradeConfig={tradeConfig}
              selectedWallet={selectedWallet}
              token={token}
              tokenInfo={tokenInfo}
              tradePreview={tradePreview}
              tradingFees={tradingFees}
              calculateTradeAmount={calculateTradeAmount}
              onBack={goBackToConfig}
              onExecute={executeTrade}
              loading={loading}
            />
          )}

          {currentStep === 'executing' && (
            <QuickExecutingStep
              tradeConfig={tradeConfig}
              token={token}
              calculateTradeAmount={calculateTradeAmount}
            />
          )}

          {currentStep === 'success' && (
            <QuickSuccessStep
              tradeConfig={tradeConfig}
              token={token}
              calculateTradeAmount={calculateTradeAmount}
              onClose={handleClose}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Simplified Configure Step for Quick Trading
const QuickConfigureStep = ({
  tradeConfig,
  setTradeConfig,
  platformWallets,
  walletBalances,
  token,
  tokenInfo,
  userTokenBalance,
  calculateTradeAmount,
  onNext,
  loading
}) => {
  const selectedWallet = platformWallets.find(w => w.id.toString() === tradeConfig.walletId);
  const walletBalance = selectedWallet ? walletBalances[selectedWallet.id] : null;

  return (
    <div className="configure-step quick-trade">
      {/* Pre-filled Token Information Display */}
      <div className="token-info-section">
        <div className="token-display">
          <img
            src={tokenInfo?.imageUrl}
            alt={tokenInfo?.name}
            className="token-logo"
            onError={(e) => {
              e.target.src = `https://via.placeholder.com/48x48/6366f1/ffffff?text=${tokenInfo?.symbol?.charAt(0) || '?'}`;
            }}
          />
          <div className="token-details">
            <h3>{tokenInfo?.symbol}</h3>
            <p>{tokenInfo?.name}</p>
            <div className="token-metrics">
              <span className="price">
                ${tokenInfo?.priceUSD ? (parseFloat(tokenInfo.priceUSD) < 0.001 ? parseFloat(tokenInfo.priceUSD).toExponential(3) : parseFloat(tokenInfo.priceUSD).toFixed(6)) : 'N/A'}
              </span>
              {tokenInfo?.marketCap && (
                <span className="market-cap">
                  MC: ${(parseFloat(tokenInfo.marketCap) / 1000000).toFixed(2)}M
                </span>
              )}
              <span className="balance-value" style={{
                marginTop: 78, color: 'blue'
              }}>  {parseFloat(userTokenBalance).toFixed(6)} {tokenInfo?.symbol}
              </span>

            </div>
          </div>
        </div>

        {/* Show user balance for sell orders */}
        {tradeConfig.type === 'sell' && userTokenBalance !== null && (
          <div className="user-balance-info">
            <span className="balance-label">Available Balance:</span>
            <span className="balance-value">{parseFloat(userTokenBalance).toFixed(6)} {tokenInfo?.symbol}</span>
          </div>
        )}
      </div>

      {/* Wallet Selection */}
      <div className="form-section">
        <label className="form-label">Trading Wallet</label>
        <select
          className="form-select"
          value={tradeConfig.walletId}
          onChange={(e) => setTradeConfig(prev => ({ ...prev, walletId: e.target.value }))}
        >
          <option value="">Select wallet...</option>
          {platformWallets.map(wallet => (
            <option key={wallet.id} value={wallet.id}>
              {wallet.name || `Wallet ${wallet.id}`} - {walletBalances[wallet.id]?.sol?.toFixed(4) || '0'} SOL
            </option>
          ))}
        </select>

        {selectedWallet && (
          <div className="wallet-details">
            <CopyableAddress
              address={selectedWallet.wallet_address}
              truncate={true}
              truncateLength={8}
            />
            <SafeBalanceDisplay compact={true} walletId={selectedWallet?.id} />
          </div>
        )}
      </div>

      {/* Simplified Amount Configuration */}
      <div className="form-section">
        <label className="form-label">
          {tradeConfig.type === 'buy' ? 'SOL Amount' : `${tokenInfo?.symbol} Amount`}
        </label>

        <div className="amount-type-selector">
          <button
            className={`amount-type-btn ${tradeConfig.amountType === 'custom' ? 'active' : ''}`}
            onClick={() => setTradeConfig(prev => ({ ...prev, amountType: 'custom' }))}
          >
            Custom Amount
          </button>
          <button
            className={`amount-type-btn ${tradeConfig.amountType === 'percentage' ? 'active' : ''}`}
            onClick={() => setTradeConfig(prev => ({ ...prev, amountType: 'percentage' }))}
          >
            Percentage
          </button>
        </div>

        {tradeConfig.amountType === 'custom' ? (
          <div className="custom-amount-input">
            <input
              type="number"
              className="form-input"
              value={tradeConfig.amount}
              onChange={(e) => setTradeConfig(prev => ({ ...prev, amount: e.target.value }))}
              placeholder={`Enter ${tradeConfig.type === 'buy' ? 'SOL' : tokenInfo?.symbol || 'token'} amount`}
              step="0.000001"
              min="0"
              max={tradeConfig.type === 'sell' ? userTokenBalance : undefined}
            />
            <span className="input-suffix">
              {tradeConfig.type === 'buy' ? 'SOL' : tokenInfo?.symbol || 'TOKEN'}
            </span>
          </div>
        ) : (
          <div className="percentage-controls">
            <div className="percentage-slider">
              <input
                type="range"
                min="1"
                max="100"
                value={tradeConfig.percentage}
                onChange={(e) => setTradeConfig(prev => ({ ...prev, percentage: parseInt(e.target.value) }))}
                className="slider"
              />
              <div className="percentage-display">{tradeConfig.percentage}%</div>
            </div>
            <div className="percentage-buttons">
              {[25, 50, 75, 100].map(pct => (
                <button
                  key={pct}
                  className={`percentage-btn ${tradeConfig.percentage === pct ? 'active' : ''}`}
                  onClick={() => setTradeConfig(prev => ({ ...prev, percentage: pct }))}
                >
                  {pct === 100 ? 'MAX' : `${pct}%`}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="calculated-amount">
          <span className="amount-label">Trade Amount:</span>
          <span className="amount-value">
            {parseFloat(calculateTradeAmount()).toFixed(6)} {tradeConfig.type === 'buy' ? 'SOL' : tokenInfo?.symbol || 'TOKEN'}
          </span>
        </div>
      </div>

      {/* Simplified Slippage Control */}
      <div className="form-section">
        <label className="form-label">Slippage Tolerance</label>
        <div className="slippage-controls">
          <div className="slippage-presets">
            {[10, 50, 100, 300].map(bps => (
              <button
                key={bps}
                className={`slippage-btn ${tradeConfig.slippageBps === bps ? 'active' : ''}`}
                onClick={() => setTradeConfig(prev => ({ ...prev, slippageBps: bps }))}
              >
                {bps / 100}%
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Next Button */}
      <div className="form-actions">
        <button
          className="btn btn-primary"
          onClick={onNext}
          disabled={!tradeConfig.walletId || calculateTradeAmount() <= 0 || loading}
        >
          {loading ? 'Loading...' : 'Review Trade'}
        </button>
      </div>
    </div>
  );
};

// Simplified Preview Step with Real-time Price Quotes
const QuickPreviewStep = ({
  tradeConfig,
  selectedWallet,
  token,
  tokenInfo,
  tradePreview,
  tradingFees,
  calculateTradeAmount,
  onBack,
  onExecute,
  loading
}) => {
  return (
    <div className="preview-step quick-trade">
      <div className="trade-summary">
        <h3>Trade Summary</h3>

        <div className="summary-row">
          <span className="label">Action:</span>
          <span className="value">
            {tradeConfig.type === 'buy' ? 'Buy' : 'Sell'} {tokenInfo?.symbol}
          </span>
        </div>

        <div className="summary-row">
          <span className="label">Wallet:</span>
          <span className="value">
            <CopyableAddress
              address={selectedWallet?.wallet_address}
              truncate={true}
              truncateLength={8}
            />
          </span>
        </div>

        <div className="summary-row">
          <span className="label">
            {tradeConfig.type === 'buy' ? 'Spending:' : 'Selling:'}
          </span>
          <span className="value">
            {parseFloat(calculateTradeAmount()).toFixed(6)} {tradeConfig.type === 'buy' ? 'SOL' : tokenInfo?.symbol}
          </span>
        </div>

        {tradePreview && (
          <>
            <div className="summary-row">
              <span className="label">
                {tradeConfig.type === 'buy' ? 'Receiving:' : 'Getting:'}
              </span>
              <span className="value">
                {parseFloat(tradePreview.outputAmount).toFixed(6)} {tradeConfig.type === 'buy' ? tokenInfo?.symbol : 'SOL'}
              </span>
            </div>

            <div className="summary-row">
              <span className="label">Price Impact:</span>
              <span className={`value ${parseFloat(tradePreview.priceImpact) > 5 ? 'warning' : ''}`}>
                {parseFloat(tradePreview.priceImpact)?.toFixed(2)}%
              </span>
            </div>

            <div className="summary-row">
              <span className="label">Minimum Received:</span>
              <span className="value">
                {(parseFloat(tradePreview.minimumReceived) / (tradeConfig.type === 'buy' ? 1000000 : 1000000000)).toFixed(6)} {tradeConfig.type === 'buy' ? tokenInfo?.symbol : 'SOL'}
              </span>
            </div>
          </>
        )}

        <div className="summary-row">
          <span className="label">Slippage Tolerance:</span>
          <span className="value">{(tradeConfig.slippageBps / 100).toFixed(1)}%</span>
        </div>

        {tradingFees && (
          <div className="summary-row">
            <span className="label">Estimated Fees:</span>
            <span className="value">{parseFloat(tradingFees.totalFee)?.toFixed(6)} SOL</span>
          </div>
        )}
      </div>

      {parseFloat(tradePreview?.priceImpact) > 5 && (
        <div className="warning-box">
          <div className="warning-icon">⚠️</div>
          <div className="warning-text">
            <strong>High Price Impact Warning</strong>
            <p>This trade will have a {parseFloat(tradePreview.priceImpact).toFixed(2)}% price impact. Consider reducing the trade size.</p>
          </div>
        </div>
      )}

      <div className="form-actions">
        <button className="btn btn-secondary" onClick={onBack} disabled={loading}>
          Back
        </button>
        <button
          className="btn btn-primary"
          onClick={onExecute}
          disabled={loading || !tradePreview}
        >
          {loading ? 'Loading...' : `Execute ${tradeConfig.type === 'buy' ? 'Buy' : 'Sell'}`}
        </button>
      </div>
    </div>
  );
};

// Executing Step Component
const QuickExecutingStep = ({ tradeConfig, token, calculateTradeAmount }) => {
  return (
    <div className="executing-step">
      <div className="executing-animation">
        <div className="spinner"></div>
        <div className="executing-icon">⚡</div>
      </div>
      <h3>Executing Quick Trade...</h3>
      <p>
        {tradeConfig.type === 'buy' ? 'Buying' : 'Selling'} {parseFloat(calculateTradeAmount()).toFixed(6)} {tradeConfig.type === 'buy' ? 'SOL worth of' : ''} {token?.symbol}
      </p>
      <div className="progress-steps">
        <div className="step completed">✓ Validating trade parameters</div>
        <div className="step completed">✓ Getting price quote</div>
        <div className="step active">⏳ Executing on blockchain</div>
        <div className="step">⏳ Confirming transaction</div>
      </div>
    </div>
  );
};

// Success Step Component with Portfolio Update
const QuickSuccessStep = ({ tradeConfig, token, calculateTradeAmount, onClose }) => {
  return (
    <div className="success-step">
      <div className="success-animation">
        <div className="success-icon">✅</div>
      </div>
      <h3>Quick Trade Successful!</h3>
      <p>
        Successfully {tradeConfig.type === 'buy' ? 'bought' : 'sold'} {parseFloat(calculateTradeAmount()).toFixed(6)} {tradeConfig.type === 'buy' ? 'SOL worth of' : ''} {token?.symbol}
      </p>

      <div className="success-actions">
        <button className="btn btn-primary" onClick={onClose}>
          Close
        </button>
        <button className="btn btn-secondary" onClick={() => window.open(`https://solscan.io/`, '_blank')}>
          View on Solscan
        </button>
      </div>
    </div>
  );
};

export default QuickTradeModal;